import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Chip,
  Button,
  TextField,
  MenuItem,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  Pagination,
  Alert,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  DirectionsCar as CarIcon,
  Person as PersonIcon,
  Schedule as ScheduleIcon,
  AttachMoney as MoneyIcon,
  Phone as PhoneIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Group as GroupIcon,
  WhatsApp as WhatsAppIcon,
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useSocket } from '../contexts/SocketContext';
import { rideService } from '../services/rideService';
import LoadingSpinner from '../components/UI/LoadingSpinner';
import { Ride, RideFilters } from '../types';
import { formatPhoneNumber } from '../utils/phoneUtils';
import toast from 'react-hot-toast';

const Rides: React.FC = () => {
  const queryClient = useQueryClient();
  const { socket, clearNewRideCount } = useSocket();
  const [filters, setFilters] = useState<RideFilters>({
    page: 1,
    limit: 12,
  });
  const [selectedRide, setSelectedRide] = useState<Ride | null>(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [newStatus, setNewStatus] = useState('');
  const [notes, setNotes] = useState('');
  
  // Edit form state
  const [editFrom, setEditFrom] = useState('');
  const [editTo, setEditTo] = useState('');
  const [editTime, setEditTime] = useState('');
  const [editPrice, setEditPrice] = useState('');
  const [editContact, setEditContact] = useState('');
  const [editVehicleType, setEditVehicleType] = useState('');
  const [editRideType, setEditRideType] = useState('');

  const getPreferredVehicleFromList = (vehicleTypes?: string[]) => {
    if (!vehicleTypes || vehicleTypes.length === 0) {
      return '';
    }

    return [...vehicleTypes]
      .filter(Boolean)
      .sort((left, right) => right.length - left.length)[0] || '';
  };

  const getPrimaryVehicleLabel = (ride: Ride) => (
    ride.aiAnalysis?.availableVehicleType
    || getPreferredVehicleFromList(ride.aiAnalysis?.vehicleTypes)
    || ride.aiAnalysis?.vehicleType
    || ''
  );

  const getRidePhones = (ride: Ride) => {
    const phones = [
      ride.aiAnalysis?.contactInfo?.phone,
      ...(ride.aiAnalysis?.contactInfo?.alternatePhones || []),
      ride.messageInfo?.senderNumber,
      ride.contactInfo?.whatsappNumber,
    ].filter(Boolean) as string[];

    return phones.filter((phone, index) => phones.indexOf(phone) === index);
  };

  const getPrimaryDialPhone = (ride: Ride) => getRidePhones(ride)[0] || '';

  // Clear new ride count when component mounts
  useEffect(() => {
    clearNewRideCount();
    
    // Listen for real-time ride updates
    if (socket) {
      const handleNewRide = (ride: any) => {
        console.log('New ride received:', ride);
        // Invalidate and refetch rides query to update the list
        queryClient.invalidateQueries({ queryKey: ['rides'] });
      };

      const handleRideUpdate = (data: any) => {
        console.log('Ride updated:', data);
        // Invalidate and refetch rides query to update the list
        queryClient.invalidateQueries({ queryKey: ['rides'] });
      };

      socket.on('newRide', handleNewRide);
      socket.on('rideUpdate', handleRideUpdate);
      socket.on('ride:statusChanged', handleRideUpdate);

      return () => {
        socket.off('newRide', handleNewRide);
        socket.off('rideUpdate', handleRideUpdate);
        socket.off('ride:statusChanged', handleRideUpdate);
      };
    }
  }, [clearNewRideCount, socket, queryClient]);

  // Fetch rides with filters
  const { data: ridesResponse, isLoading, refetch } = useQuery({
    queryKey: ['rides', filters],
    queryFn: () => rideService.getRides(filters),
    keepPreviousData: true,
  });

  // Update ride status mutation
  const updateStatusMutation = useMutation({
    mutationFn: ({ id, status, notes }: { id: string; status: string; notes?: string }) =>
      rideService.updateRideStatus(id, status, notes),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['rides'] });
      queryClient.invalidateQueries({ queryKey: ['rideStats'] });
      toast.success('Ride status updated successfully');
      setEditDialogOpen(false);
      setSelectedRide(null);
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to update ride status');
    },
  });

  // Update ride details mutation
  const updateDetailsMutation = useMutation({
    mutationFn: ({ id, details }: { id: string; details: any }) =>
      rideService.updateRideDetails(id, details),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['rides'] });
      queryClient.invalidateQueries({ queryKey: ['rideStats'] });
      toast.success('Ride details updated successfully');
      setEditDialogOpen(false);
      setSelectedRide(null);
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to update ride details');
    },
  });

  // Delete ride mutation
  const deleteMutation = useMutation({
    mutationFn: (id: string) => rideService.deleteRide(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['rides'] });
      queryClient.invalidateQueries({ queryKey: ['rideStats'] });
      toast.success('Ride deleted successfully');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to delete ride');
    },
  });

  const handleFilterChange = (key: keyof RideFilters, value: any) => {
    setFilters(prev => ({
      ...prev,
      [key]: value,
      page: key !== 'page' ? 1 : value, // Reset to first page when changing other filters
    }));
  };

  const handleEditRide = (ride: Ride) => {
    setSelectedRide(ride);
    setNewStatus(ride.status);
    setNotes(ride.notes || '');
    
    // Populate edit form with current values
    setEditFrom(ride.aiAnalysis?.from || '');
    setEditTo(ride.aiAnalysis?.to || '');
    setEditTime(ride.aiAnalysis?.time || '');
    setEditPrice(ride.aiAnalysis?.price || '');
    setEditContact(ride.aiAnalysis?.contactInfo?.name || ride.messageInfo?.senderName || '');
    setEditVehicleType(getPrimaryVehicleLabel(ride));
    setEditRideType(ride.aiAnalysis?.rideType || '');
    
    setEditDialogOpen(true);
  };

  const handleUpdateRide = () => {
    if (!selectedRide) return;

    // Update details
    const details = {
      from: editFrom,
      to: editTo,
      time: editTime,
      price: editPrice,
      contactName: editContact,
      vehicleType: editVehicleType,
      rideType: editRideType,
    };

    updateDetailsMutation.mutate({
      id: selectedRide._id,
      details,
    });

    // Also update status and notes if changed
    if (newStatus !== selectedRide.status || notes !== (selectedRide.notes || '')) {
      updateStatusMutation.mutate({
        id: selectedRide._id,
        status: newStatus,
        notes: notes,
      });
    }
  };

  const handleDeleteRide = (rideId: string) => {
    if (window.confirm('Are you sure you want to delete this ride?')) {
      deleteMutation.mutate(rideId);
    }
  };

  const getConfidenceColor = (confidence: number) => {
    if (confidence >= 80) return 'success';
    if (confidence >= 60) return 'warning';
    return 'error';
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (isLoading && !ridesResponse) {
    return <LoadingSpinner message="Loading rides..." />;
  }

  const rides = ridesResponse?.rides || [];
  const pagination = ridesResponse?.pagination;

  
  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">
          Ride Requests
          {pagination && (
            <Typography variant="body2" color="text.secondary" component="span" sx={{ ml: 2 }}>
              ({pagination.total} total)
            </Typography>
          )}
        </Typography>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={() => refetch()}
          disabled={isLoading}
        >
          Refresh
        </Button>
      </Box>

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                size="small"
                placeholder="Search rides..."
                value={filters.search || ''}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                fullWidth
                size="small"
                select
                label="Status"
                value={filters.status || ''}
                onChange={(e) => handleFilterChange('status', e.target.value)}
              >
                <MenuItem value="">All Statuses</MenuItem>
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="qualified">Qualified</MenuItem>
                <MenuItem value="contacted">Contacted</MenuItem>
                <MenuItem value="accepted">Accepted</MenuItem>
                <MenuItem value="booked">Booked</MenuItem>
                <MenuItem value="completed">Completed</MenuItem>
                <MenuItem value="rejected">Rejected</MenuItem>
                <MenuItem value="expired">Expired</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                fullWidth
                size="small"
                type="number"
                label="Min Confidence"
                value={filters.confidence || ''}
                onChange={(e) => handleFilterChange('confidence', e.target.value ? parseFloat(e.target.value) : undefined)}
                inputProps={{ min: 0, max: 100 }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                fullWidth
                size="small"
                select
                label="Per Page"
                value={filters.limit || 12}
                onChange={(e) => handleFilterChange('limit', parseInt(e.target.value))}
              >
                <MenuItem value={6}>6</MenuItem>
                <MenuItem value={12}>12</MenuItem>
                <MenuItem value={24}>24</MenuItem>
                <MenuItem value={48}>48</MenuItem>
              </TextField>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Rides Grid */}
      {rides.length === 0 ? (
        <Alert severity="info" sx={{ textAlign: 'center' }}>
          No ride requests found. {filters.search || filters.status ? 'Try adjusting your filters.' : 'New requests will appear here automatically.'}
        </Alert>
      ) : (
        <>
          <Grid container spacing={3}>
            {rides.map((ride) => (
              <Grid item xs={12} sm={6} md={4} key={ride._id}>
                <Card sx={{ 
                  height: '100%', 
                  display: 'flex', 
                  flexDirection: 'column',
                  border: '1px solid #e0e0e0',
                  borderRadius: 2,
                  overflow: 'hidden',
                  '&:hover': { 
                    boxShadow: 3,
                    transform: 'translateY(-2px)',
                    transition: 'all 0.2s ease-in-out'
                  }
                }}>
                  {/* Header with Route and Source Badge */}
                  <Box sx={{ 
                    bgcolor: ride.aiAnalysis?.confidence >= 80 ? '#e8f5e8' : ride.aiAnalysis?.confidence >= 60 ? '#fff3e0' : '#ffebee',
                    p: 2,
                    borderBottom: '1px solid #e0e0e0'
                  }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                      {/* Status temporarily hidden as requested */}
                      {/* <Chip
                        label={ride.status}
                        color={getStatusColor(ride.status) as any}
                        size="small"
                      /> */}
                      {/* Source Badge */}
                      <Chip
                        label={ride.aiAnalysis?.source === 'ai' ? '🤖 AI' : '⚡ Filter'}
                        color={ride.aiAnalysis?.source === 'ai' ? 'secondary' : 'primary'}
                        size="small"
                        sx={{ fontWeight: 500 }}
                      />
                    </Box>
                    
                    {/* Route - Primary Focus */}
                    <Typography variant="h6" sx={{ 
                      fontWeight: 700, 
                      color: '#1976d2',
                      mb: 1,
                      lineHeight: 1.2
                    }}>
                      {(ride.aiAnalysis?.from || 'Unknown').replace(/^(Need|Sedan)\s+/, '')}
                      <Box component="span" sx={{ mx: 1, color: '#666', fontSize: '1.1rem' }}>→</Box>
                      {ride.aiAnalysis?.to || 'Unknown'}
                    </Typography>
                    
                    {/* Time and Price Row - Secondary Focus */}
                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                      {/* Time */}
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <ScheduleIcon sx={{ fontSize: 16, mr: 0.5, color: '#1976d2' }} />
                        <Typography variant="body1" sx={{ fontWeight: 600, color: '#1976d2' }}>
                          {ride.aiAnalysis?.time || 'N/A'}
                        </Typography>
                      </Box>
                      
                      {/* Price */}
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <MoneyIcon sx={{ fontSize: 16, mr: 0.5, color: '#2e7d32' }} />
                        <Typography variant="body1" sx={{ fontWeight: 600, color: '#2e7d32' }}>
                          {ride.aiAnalysis?.price || 'N/A'}
                        </Typography>
                      </Box>
                      
                      {/* Vehicle Type */}
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <CarIcon sx={{ fontSize: 16, mr: 0.5, color: '#1976d2' }} />
                        <Box>
                          <Typography variant="body2" sx={{ 
                            backgroundColor: '#e3f2fd', 
                            color: '#1976d2', 
                            px: 1, 
                            py: 0.5, 
                            borderRadius: 1,
                            fontSize: '0.75rem',
                            fontWeight: 500,
                            textTransform: 'uppercase',
                            display: 'inline-block'
                          }}>
                            {getPrimaryVehicleLabel(ride) || 'N/A'}
                          </Typography>
                          {ride.aiAnalysis?.requestedVehicleType && ride.aiAnalysis.requestedVehicleType !== getPrimaryVehicleLabel(ride) && (
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                              Requested: {ride.aiAnalysis.requestedVehicleType}
                            </Typography>
                          )}
                        </Box>
                      </Box>

                      {/* Ride Type */}
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Typography variant="body2" sx={{ 
                          backgroundColor: '#fff3e0', 
                          color: '#f57c00', 
                          px: 1, 
                          py: 0.5, 
                          borderRadius: 1,
                          fontSize: '0.75rem',
                          fontWeight: 500,
                          textTransform: 'capitalize'
                        }}>
                          {ride.aiAnalysis?.rideType || 'N/A'}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>

                  <CardContent sx={{ flexGrow: 1, p: 2 }}>
                      {/* Contact Information - Grouped properly */}
                    <Box sx={{ mb: 2 }}>
                      {/* Contact Person */}
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, gap: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                          <PersonIcon sx={{ mr: 1, fontSize: 16, color: '#666' }} />
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {ride.aiAnalysis?.contactInfo?.name || ride.messageInfo?.senderName || 'N/A'}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <PhoneIcon sx={{ mr: 1, fontSize: 16, color: '#666' }} />
                          <Box>
                            <Typography variant="body2">
                              {formatPhoneNumber(getPrimaryDialPhone(ride)) || 'N/A'}
                            </Typography>
                            {ride.aiAnalysis?.contactInfo?.alternatePhones && ride.aiAnalysis.contactInfo.alternatePhones.length > 0 && (
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                Alt: {ride.aiAnalysis.contactInfo.alternatePhones.map((phone) => formatPhoneNumber(phone)).join(', ')}
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      </Box>                      {/* WhatsApp Context - Grouped together */}
                      {(ride.messageInfo?.groupName || ride.messageInfo?.accountName) && (
                        <Box sx={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          gap: 2, 
                          p: 1, 
                          backgroundColor: '#f5f5f5', 
                          borderRadius: 1,
                          mb: 1 
                        }}>
                          {ride.messageInfo?.groupName && ride.messageInfo.groupName !== 'Direct Message' && (
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <GroupIcon sx={{ mr: 1, fontSize: 16, color: '#666' }} />
                              <Typography variant="body2" color="primary" sx={{ fontWeight: 500 }}>
                                {ride.messageInfo.groupName}
                              </Typography>
                            </Box>
                          )}
                          
                          {ride.messageInfo?.accountName && (
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <WhatsAppIcon sx={{ mr: 1, fontSize: 16, color: '#25d366' }} />
                              <Typography variant="body2" color="text.secondary">
                                {ride.messageInfo.accountName}
                              </Typography>
                            </Box>
                          )}
                        </Box>
                      )}
                    </Box>

                    {/* Original Message */}
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                        Original Message:
                      </Typography>
                      <Typography variant="body2" sx={{ 
                        backgroundColor: 'grey.100', 
                        p: 1.5, 
                        borderRadius: 1,
                        fontSize: '0.8rem',
                        maxHeight: '80px',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        borderLeft: '3px solid #1976d2'
                      }}>
                        {ride.originalMessage}
                      </Typography>
                    </Box>

                    {/* Footer */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        <Chip
                          label={`${Math.round(ride.aiAnalysis?.confidence || 0)}%`}
                          color={getConfidenceColor(ride.aiAnalysis?.confidence || 0) as any}
                          size="small"
                          variant="outlined"
                        />
                        {ride.aiAnalysis?.type && (
                          <Chip
                            label={ride.aiAnalysis.type.replace('_', ' ')}
                            variant="outlined"
                            size="small"
                            sx={{ textTransform: 'capitalize' }}
                          />
                        )}
                      </Box>
                      <Typography variant="caption" color="text.secondary">
                        {formatDate(ride.createdAt)}
                      </Typography>
                    </Box>

                    {ride.notes && (
                      <Box sx={{ mt: 2 }}>
                        <Typography variant="caption" color="text.secondary">
                          Notes: {ride.notes}
                        </Typography>
                      </Box>
                    )}
                  </CardContent>
                  
                  {/* Action Buttons */}
                  <Box sx={{ p: 2, pt: 0, display: 'flex', gap: 1 }}>
                    <Tooltip title="Edit Ride">
                      <IconButton size="small" onClick={() => handleEditRide(ride)}>
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete Ride">
                      <IconButton 
                        size="small" 
                        color="error"
                        onClick={() => handleDeleteRide(ride._id)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                    <Box sx={{ flexGrow: 1 }} />
                    <Button
                      variant="contained"
                      size="small"
                      onClick={() => {
                        const phoneNumber = getPrimaryDialPhone(ride).replace(/\D/g, '');
                        if (phoneNumber) {
                          window.open(`https://wa.me/${phoneNumber}`, '_blank');
                        }
                      }}
                      startIcon={<WhatsAppIcon />}
                      sx={{ 
                        bgcolor: '#25d366', 
                        '&:hover': { bgcolor: '#20b954' },
                        minWidth: 'auto',
                        px: 1
                      }}
                    >
                      Chat
                    </Button>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => {
                        const phoneNumber = getPrimaryDialPhone(ride).replace(/\D/g, '');
                        if (phoneNumber) {
                          window.open(`tel:${phoneNumber}`);
                        }
                      }}
                      startIcon={<PhoneIcon />}
                      sx={{ minWidth: 'auto', px: 1 }}
                    >
                      Call
                    </Button>
                  </Box>
                </Card>
              </Grid>
            ))}
          </Grid>

          {/* Pagination */}
          {pagination && pagination.pages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
              <Pagination
                count={pagination.pages}
                page={pagination.page}
                onChange={(event, page) => handleFilterChange('page', page)}
                color="primary"
              />
            </Box>
          )}
        </>
      )}

      {/* Edit Ride Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Ride</DialogTitle>
        <DialogContent>
          {selectedRide && (
            <Box sx={{ pt: 1 }}>
              {/* Original Info */}
              <Box sx={{ mb: 3, p: 2, backgroundColor: 'grey.50', borderRadius: 1 }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Original Message Information:
                </Typography>
                {selectedRide.messageInfo?.groupName && selectedRide.messageInfo.groupName !== 'Direct Message' && (
                  <Typography variant="body2" color="primary" gutterBottom>
                    Group: {selectedRide.messageInfo.groupName}
                  </Typography>
                )}
                {selectedRide.messageInfo?.accountName && (
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    WhatsApp Account: {selectedRide.messageInfo.accountName}
                  </Typography>
                )}
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Phone: {formatPhoneNumber(selectedRide.messageInfo?.senderNumber || selectedRide.contactInfo?.whatsappNumber)}
                </Typography>
              </Box>

              {/* Editable Fields */}
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="From Location"
                    value={editFrom}
                    onChange={(e) => setEditFrom(e.target.value)}
                    placeholder="Enter pickup location"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="To Location"
                    value={editTo}
                    onChange={(e) => setEditTo(e.target.value)}
                    placeholder="Enter destination"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Time"
                    value={editTime}
                    onChange={(e) => setEditTime(e.target.value)}
                    placeholder="e.g., 2 PM, tomorrow, etc."
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Price"
                    value={editPrice}
                    onChange={(e) => setEditPrice(e.target.value)}
                    placeholder="e.g., $50, 50 USD, etc."
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Contact Name"
                    value={editContact}
                    onChange={(e) => setEditContact(e.target.value)}
                    placeholder="Enter contact name"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Vehicle Type"
                    value={editVehicleType}
                    onChange={(e) => setEditVehicleType(e.target.value)}
                    placeholder="e.g., sedan, SUV, etc."
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Ride Type"
                    value={editRideType}
                    onChange={(e) => setEditRideType(e.target.value)}
                    placeholder="e.g., one way, exchange duty, etc."
                  />
                </Grid>
              </Grid>
              
              <FormControl fullWidth sx={{ mt: 2, mb: 2 }}>
                <InputLabel>Status</InputLabel>
                <Select
                  value={newStatus}
                  label="Status"
                  onChange={(e) => setNewStatus(e.target.value)}
                >
                  <MenuItem value="pending">Pending</MenuItem>
                  <MenuItem value="contacted">Contacted</MenuItem>
                  <MenuItem value="booked">Booked</MenuItem>
                  <MenuItem value="completed">Completed</MenuItem>
                  <MenuItem value="rejected">Rejected</MenuItem>
                </Select>
              </FormControl>

              <TextField
                fullWidth
                multiline
                rows={3}
                label="Notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Add any notes about this ride..."
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={handleUpdateRide} 
            variant="contained"
            disabled={updateStatusMutation.isLoading}
          >
            {updateStatusMutation.isLoading ? 'Updating...' : 'Update'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Rides;
